import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Field, Fieldset } from "@headlessui/react";
import Modal from "../../Modal";
import Button from "../../Button";
import FormSelect from "../../Form/FormSelect";
import { Alert } from "../../../Alert";
import {
  addLocalNotification,
  clearInitAccountStatus,
  clearUpdateBusiness,
  requestGetBusiness,
  requestInitAccount,
  requestListAccount,
  requestUpdateBusiness,
  useAccountingDispatch,
  useAccountingState,
  useAuthDispatch,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  useNotificationDispatch,
} from "../../../../Context";
import { getErrorMessages } from "../../../../Utils";
import {
  BUSINESS_CATEGORIES,
  BUSINESS_ENTITY_TYPES,
  BUSINESS_TYPES_OPTIONS,
} from "../../../../Constants/business";

// Skippable prompt shown when a business has no chart of accounts yet:
// generate the default chart (POST /init), upload a template, or skip.
// If the business profile misses type/structure/category, it asks for them
// first (optional) so the seeded chart better matches the business.
const InitAccountPromptModal = ({
  open,
  businessID,
  onSkip,
  onInitialized,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Only react to init results triggered from this prompt — submitError and
  // initAccountStatus are shared accounting state.
  const [attempted, setAttempted] = useState(false);
  // True while waiting for the profile update that precedes the init call.
  const [updatingProfile, setUpdatingProfile] = useState(false);

  const [typeSel, setTypeSel] = useState(null);
  const [structureSel, setStructureSel] = useState(null);
  const [categorySel, setCategorySel] = useState(null);

  const accountingDispatch = useAccountingDispatch();
  const businessDispatch = useBusinessTransactionDispatch();
  const notificationDispatch = useNotificationDispatch();
  const authDispatch = useAuthDispatch();
  const { user } = useAuthState();
  const { initAccountLoading, initAccountStatus, submitError } =
    useAccountingState();
  const {
    business,
    loading: businessLoading,
    updateBusiness,
    error: businessSubmitError,
  } = useBusinessTransactionState();

  // Load the profile so we know which fields are missing.
  useEffect(() => {
    if (businessID) {
      requestGetBusiness(businessDispatch, { id: businessID });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessID]);

  const businessLoaded = business?.id === businessID;
  const effectiveType = typeSel?.value || business?.type || "";
  const missingType = businessLoaded && !business.type;
  // Structure only applies to incorporated (BUSINESS) entities.
  const missingStructure =
    businessLoaded && !business.structure && effectiveType === "BUSINESS";
  const missingCategory = businessLoaded && !business.category;
  const needsProfile = missingType || missingStructure || missingCategory;

  // Profile update done → run the actual init.
  useEffect(() => {
    if (updatingProfile && updateBusiness?.id === businessID) {
      // Sync the freshly-saved type/structure/category into auth state so the
      // sidebar header reflects them immediately (the newly created business is
      // the active main business).
      if (updateBusiness.id === user?.mainBusiness?.id) {
        authDispatch({
          type: "UPDATE_MAIN_BUSINESS",
          payload: { business: updateBusiness },
        });
      }
      clearUpdateBusiness(businessDispatch);
      setUpdatingProfile(false);
      requestInitAccount(accountingDispatch, { businessID });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateBusiness?.id, updatingProfile]);

  // Profile update failed → stop waiting, error shows below.
  useEffect(() => {
    if (updatingProfile && businessSubmitError) {
      setUpdatingProfile(false);
    }
  }, [businessSubmitError, updatingProfile]);

  useEffect(() => {
    if (attempted && initAccountStatus === "SUCCESS") {
      clearInitAccountStatus(accountingDispatch);
      addLocalNotification(notificationDispatch, {
        id: `init-account-${businessID}`,
        type: "success",
        title: "common.success",
        body: "initAccountPrompt.success",
        showFull: true,
      });
      // Refresh the shared accounts list so empty-account guards re-evaluate.
      requestListAccount(accountingDispatch, { businessID, limit: 100 });
      if (onInitialized) onInitialized();
      onSkip();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initAccountStatus, attempted]);

  const handleInitAccount = () => {
    setAttempted(true);
    // Persist any profile answers first — the backend reads structure/type/
    // category off the Business record when it builds the chart.
    const updates = {};
    if (missingType && typeSel?.value) updates.type = typeSel.value;
    if (missingStructure && structureSel?.value)
      updates.structure = structureSel.value;
    if (missingCategory && categorySel?.value)
      updates.category = categorySel.value;

    if (Object.keys(updates).length > 0) {
      setUpdatingProfile(true);
      requestUpdateBusiness(businessDispatch, {
        input: { id: businessID, ...updates },
      });
    } else {
      requestInitAccount(accountingDispatch, { businessID });
    }
  };

  const initLoading = initAccountLoading || updatingProfile || businessLoading;

  return (
    <Modal
      open={open}
      onClose={onSkip}
      title="initAccountPrompt.title"
      footer={
        <div className="flex flex-col sm:flex-row justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            disabled={initLoading}
            onClick={onSkip}
          >
            {t("skip")}
          </Button>
          <Button
            type="button"
            variant="secondary"
            color="blue"
            disabled={initLoading}
            onClick={() => navigate(`/account/import?id=${businessID}`)}
          >
            {t("uploadTemplate")}
          </Button>
          <Button
            type="button"
            variant="primary"
            loading={initLoading}
            onClick={handleInitAccount}
          >
            {t("createNow")}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {attempted && (submitError || businessSubmitError) && (
          <Alert
            type="error"
            title="Error"
            // getErrorMessages parses the API body itself and survives a
            // non-JSON one (an HTML 502), which JSON.parse here threw on.
            messages={getErrorMessages(submitError || businessSubmitError)}
          />
        )}
        <p className="text-sm text-gray-700">
          {t("initAccountPrompt.description")}
        </p>
        {/* Reserve space with a skeleton until the profile is loaded so the
            modal height does not jump when the missing-field selects appear. */}
        {!businessLoaded && (
          <div className="animate-pulse space-y-4">
            <div className="h-3 w-3/4 rounded bg-gray-200" />
            <div className="space-y-2">
              <div className="h-3 w-24 rounded bg-gray-200" />
              <div className="h-9 w-full rounded bg-gray-200" />
            </div>
            <div className="space-y-2">
              <div className="h-3 w-24 rounded bg-gray-200" />
              <div className="h-9 w-full rounded bg-gray-200" />
            </div>
          </div>
        )}
        {businessLoaded && needsProfile && (
          <>
            <p className="text-sm text-gray-500">
              {t("initAccountPrompt.profileHint")}
            </p>
            <Fieldset
              disabled={initLoading}
              className="space-y-4 data-[disabled]:opacity-50"
            >
              {missingType && (
                <Field>
                  <FormSelect
                    label="business.fields.type"
                    id="initType"
                    name="initType"
                    options={BUSINESS_TYPES_OPTIONS}
                    value={typeSel}
                    onChange={setTypeSel}
                    isClearable
                    hint={t("common.optional")}
                  />
                </Field>
              )}
              {missingStructure && (
                <Field>
                  <FormSelect
                    label="business.fields.entityType"
                    id="initEntityType"
                    name="initEntityType"
                    options={BUSINESS_ENTITY_TYPES}
                    placeholder={t("business.placeholders.entityType")}
                    value={structureSel}
                    onChange={setStructureSel}
                    isClearable
                    searchable
                    hint={t("common.optional")}
                  />
                </Field>
              )}
              {missingCategory && (
                <Field>
                  <FormSelect
                    label={t("business.fields.category", {
                      defaultValue: "Kategori Usaha",
                    })}
                    id="initCategory"
                    name="initCategory"
                    options={BUSINESS_CATEGORIES}
                    placeholder={t("business.placeholders.category", {
                      defaultValue: "Pilih Kategori Usaha",
                    })}
                    value={categorySel}
                    onChange={setCategorySel}
                    isClearable
                    searchable
                    hint={t("common.optional")}
                  />
                </Field>
              )}
            </Fieldset>
          </>
        )}
        <p className="text-sm text-gray-500">{t("startWithProvidedCOA")}</p>
      </div>
    </Modal>
  );
};

export default InitAccountPromptModal;
